/**
 * Professional math utilities to replace Math.js
 * Lightweight alternative with the same API and behavior
 */

export type RoundingMode =
  | 'round' // Round to nearest, ties away from zero
  | 'floor' // Round down (towards negative infinity)
  | 'ceil' // Round up (towards positive infinity)
  | 'trunc' // Round towards zero
  | 'half-up' // Round to nearest, ties up
  | 'half-down' // Round to nearest, ties down
  | 'half-even'; // Round to nearest, ties to even (banker's rounding)

/**
 * Round a number to specified decimal places with various rounding modes
 * Compatible with Math.js round function
 */
export function round(
  value: number,
  decimals: number = 0,
  mode: RoundingMode = 'round',
): number {
  if (!isFinite(value) || !isFinite(decimals)) {
    return NaN;
  }

  const factor = Math.pow(10, decimals);
  const scaled = value * factor;

  let result: number;

  switch (mode) {
    case 'round':
      // Default JavaScript rounding (ties away from zero)
      result = Math.round(scaled);
      break;

    case 'floor':
      result = Math.floor(scaled);
      break;

    case 'ceil':
      result = Math.ceil(scaled);
      break;

    case 'trunc':
      result = Math.trunc(scaled);
      break;

    case 'half-up':
      // Round ties up (0.5 -> 1, -0.5 -> 0)
      result = scaled >= 0 ? Math.floor(scaled + 0.5) : Math.ceil(scaled - 0.5);
      break;

    case 'half-down':
      // Round ties down (0.5 -> 0, -0.5 -> -1)
      result = scaled >= 0 ? Math.ceil(scaled - 0.5) : Math.floor(scaled + 0.5);
      break;

    case 'half-even': {
      // Banker's rounding (round to nearest even)
      const rounded = Math.round(scaled);
      const diff = Math.abs(scaled - rounded);

      if (diff === 0.5) {
        // If exactly halfway, round to even
        result = rounded % 2 === 0 ? rounded : rounded - Math.sign(rounded);
      } else {
        result = rounded;
      }
      break;
    }

    default:
      throw new Error(`Unknown rounding mode: ${mode}`);
  }

  return result / factor;
}

/**
 * Additional utility functions to match common Math.js usage patterns
 */

export function roundTo(value: number, precision: number): number {
  return round(value, precision);
}

export function roundToSignificant(value: number, digits: number): number {
  if (value === 0) return 0;

  const magnitude = Math.floor(Math.log10(Math.abs(value)));
  const factor = Math.pow(10, digits - magnitude - 1);

  return Math.round(value * factor) / factor;
}

export function roundCurrency(value: number, currency: string = 'USD'): number {
  // Most currencies use 2 decimal places, but some use 0 or 3
  const currencyDecimals: Record<string, number> = {
    JPY: 0, // Japanese Yen
    KRW: 0, // Korean Won
    BHD: 3, // Bahraini Dinar
    JOD: 3, // Jordanian Dinar
    KWD: 3, // Kuwaiti Dinar
    OMR: 3, // Omani Rial
    TND: 3, // Tunisian Dinar
  };

  const decimals = currencyDecimals[currency.toUpperCase()] ?? 2;
  return round(value, decimals);
}

export function roundPercentage(value: number, decimals: number = 2): number {
  return round(value, decimals);
}

/**
 * Batch rounding for arrays
 */
export function roundArray(
  values: number[],
  decimals: number = 0,
  mode: RoundingMode = 'round',
): number[] {
  return values.map((value) => round(value, decimals, mode));
}

/**
 * Format rounded number as string with consistent decimal places
 */
export function roundAndFormat(
  value: number,
  decimals: number = 0,
  mode: RoundingMode = 'round',
): string {
  const rounded = round(value, decimals, mode);
  return rounded.toFixed(decimals);
}

/**
 * Safe division with rounding
 */
export function divideAndRound(
  dividend: number,
  divisor: number,
  decimals: number = 0,
): number {
  if (divisor === 0) {
    throw new Error('Division by zero');
  }
  return round(dividend / divisor, decimals);
}

/**
 * Common math operations with rounding
 */
export const math = {
  round,
  roundTo,
  roundToSignificant,
  roundCurrency,
  roundPercentage,
  roundArray,
  roundAndFormat,
  divideAndRound,

  // Additional utilities
  add: (a: number, b: number, decimals?: number) =>
    decimals !== undefined ? round(a + b, decimals) : a + b,

  subtract: (a: number, b: number, decimals?: number) =>
    decimals !== undefined ? round(a - b, decimals) : a - b,

  multiply: (a: number, b: number, decimals?: number) =>
    decimals !== undefined ? round(a * b, decimals) : a * b,

  divide: (a: number, b: number, decimals?: number) =>
    decimals !== undefined ? divideAndRound(a, b, decimals) : a / b,
};

// Export default for easy importing
export default math;
